<template>
    <div class="lunchDayMeals">
        <h3 class="lunchDayMeals__title">{{ dayLabel }}</h3>

        <p v-if="includedLunches > 0" class="section__infos">
            <svg viewBox="0 0 24 24">
                <use xlink:href="#information"></use>
            </svg>
            <span>{{ labelIncluded }}</span>
        </p>

        <input type="hidden" :name="'lunches[' + configDayId + ']'" :value="meals.length">

        <div class="lunchDayMeals__list">
            <div
                v-for="(meal, index) in meals"
                :key="'meal_' + configDayId + '_' + index"
                class="lunchDayMeals__row">

                <input type="hidden" :name="namePrefix(index) + '[day_id]'" :value="configDayId">

                <div class="lunchDayMeals__row-header">
                    <strong class="lunchDayMeals__row-title">{{ labelMeal }} #{{ index + 1 }}</strong>
                    <span v-if="index < includedLunches" class="lunchDayMeals__offered-tag">
                        <slot name="offered-label">Offert</slot>
                    </span>
                    <button type="button" class="btn-medium-grey lunchDayMeals__delete" @click="deleteMeal(index)">
                        <slot name="delete-label">Supprimer</slot>
                    </button>
                </div>

                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group">
                            <label :for="'lunch_firstname_' + configDayId + '_' + index">
                                <slot name="firstname-label">Prénom</slot>
                            </label>
                            <input
                                type="text"
                                :id="'lunch_firstname_' + configDayId + '_' + index"
                                :name="namePrefix(index) + '[firstname]'"
                                v-model="meal.firstname"
                                @keydown.enter.prevent>
                        </div>
                    </div>
                    <div class="col-md-6">
                        <div class="form-group">
                            <label :for="'lunch_lastname_' + configDayId + '_' + index">
                                <slot name="lastname-label">Nom</slot>
                            </label>
                            <input
                                type="text"
                                :id="'lunch_lastname_' + configDayId + '_' + index"
                                :name="namePrefix(index) + '[lastname]'"
                                v-model="meal.lastname"
                                @keydown.enter.prevent>
                        </div>
                    </div>
                </div>

                <div class="lunchDayMeals__restrictions" data-validation="required">
                    <label class="lunchDayMeals__restrictions-label">
                        <slot name="restrictions-section-label">Indications alimentaires</slot>
                    </label>
                    <input
                        type="text"
                        class="lunchDayMeals__restrictions-validator"
                        tabindex="-1"
                        aria-hidden="true"
                        required
                        :value="restrictionPickedFor(meal) ? '1' : ''"
                        @invalid="onRestrictionInvalid($event)"
                        @input.prevent="() => {}">
                    <div class="row">
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_no_restriction_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[no_restriction]'"
                                       v-model="meal.no_restriction"
                                       value="1">
                                <label :for="'lunch_no_restriction_' + configDayId + '_' + index">
                                    <slot name="no_restriction-label">Aucune restriction</slot>
                                </label>
                            </div>
                        </div>
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_vegan_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[vegan]'"
                                       v-model="meal.vegan"
                                       value="1">
                                <label :for="'lunch_vegan_' + configDayId + '_' + index">
                                    <slot name="vegan-label">Vegan</slot>
                                </label>
                            </div>
                        </div>
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_vegetarian_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[vegetarian]'"
                                       v-model="meal.vegetarian"
                                       value="1">
                                <label :for="'lunch_vegetarian_' + configDayId + '_' + index">
                                    <slot name="vegetarian-label">Végétarien</slot>
                                </label>
                            </div>
                        </div>
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_gluten_free_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[gluten_free]'"
                                       v-model="meal.gluten_free"
                                       value="1">
                                <label :for="'lunch_gluten_free_' + configDayId + '_' + index">
                                    <slot name="gluten_free-label">Sans gluten</slot>
                                </label>
                            </div>
                        </div>
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_lactose_free_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[lactose_free]'"
                                       v-model="meal.lactose_free"
                                       value="1">
                                <label :for="'lunch_lactose_free_' + configDayId + '_' + index">
                                    <slot name="lactose_free-label">Sans lactose</slot>
                                </label>
                            </div>
                        </div>
                        <div class="col-md-3 col-sm-6">
                            <div class="form-group form-group--inline">
                                <input type="checkbox"
                                       :id="'lunch_nut_free_' + configDayId + '_' + index"
                                       :name="namePrefix(index) + '[nut_free]'"
                                       v-model="meal.nut_free"
                                       value="1">
                                <label :for="'lunch_nut_free_' + configDayId + '_' + index">
                                    <slot name="nut_free-label">Sans fruits à coque</slot>
                                </label>
                            </div>
                        </div>
                    </div>
                    <div class="row">
                        <div class="col-12">
                            <div class="form-group">
                                <label :for="'lunch_allergies_' + configDayId + '_' + index">
                                    <slot name="allergies-label">Allergies</slot>
                                </label>
                                <input
                                    type="text"
                                    :id="'lunch_allergies_' + configDayId + '_' + index"
                                    :name="namePrefix(index) + '[allergies]'"
                                    v-model="meal.allergies"
                                    @keydown.enter.prevent>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <p v-if="meals.length === 0" class="lunchDayMeals__empty">
                <slot name="empty-label">Aucun repas réservé pour ce jour.</slot>
            </p>
        </div>

        <button type="button" class="btn-blue lunchDayMeals__add" @click="addMeal">
            <slot name="add-label">Ajouter un repas</slot>
        </button>
    </div>
</template>

<style lang="scss" scoped>
    .lunchDayMeals {
        margin-bottom: 30px;

        &__title {
            margin-bottom: 8px;
        }

        &__list {
            margin: 16px 0;
        }

        &__row {
            border: 1px solid #dfdfdf;
            border-radius: 4px;
            padding: 16px 20px;
            margin-bottom: 12px;
        }

        &__row-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 12px;
            gap: 8px;
        }

        &__offered-tag {
            display: inline-block;
            padding: 2px 10px;
            font-size: 12px;
            font-weight: 600;
            color: #1c6b3b;
            background-color: #e6f4ec;
            border-radius: 12px;
            margin-left: auto;
            margin-right: 8px;
        }

        &__restrictions {
            margin-top: 12px;
            padding-top: 12px;
            border-top: 1px dashed #dfdfdf;
        }

        &__restrictions-label {
            display: block;
            font-weight: 600;
            margin-bottom: 6px;
        }

        // Visually-hidden but kept in the layout so HTML5 validation can focus
        // it when no restriction is picked. Width: 1px is enough for the browser
        // to scroll/anchor the validation tooltip to it.
        &__restrictions-validator {
            position: absolute;
            opacity: 0;
            height: 1px;
            width: 1px;
            padding: 0;
            border: 0;
            margin: -1px;
            overflow: hidden;
            pointer-events: none;
        }

        &__empty {
            color: #6b7280;
            font-style: italic;
            padding: 12px 0;
        }

        &__delete {
            transform: none;
        }

        // Center the checkbox vertically with its label.
        // The global `input[type='checkbox']` is `position: absolute`, which leaves
        // it pinned to the top of the form-group while the label has its own height
        // (block + margin-bottom from the global label rule). Override here to keep
        // them on the same baseline inside an inline group.
        ::v-deep .form-group--inline {
            position: relative;
            min-height: 30px;
            margin: 8px 0;

            input[type='checkbox'] {
                position: relative;
                top: 0;
                left: 0;
                margin: 0;
                flex-shrink: 0;
            }

            label {
                display: inline-block;
                padding-left: 8px;
                margin-bottom: 0;
            }
        }
    }
</style>

<script>
    function isTruthyBool(v) {
        return v === true || v === 1 || v === '1' || v === 'true';
    }

    export default {
        props: {
            configDayId: {
                type: [Number, String],
                required: true,
            },
            dayLabel: {
                type: String,
                default: '',
            },
            includedLunches: {
                type: Number,
                default: 0,
            },
            labelIncluded: {
                type: String,
                default: '',
            },
            labelMeal: {
                type: String,
                default: 'Repas',
            },
            oldMeals: {
                type: [Array, Object],
                default: () => [],
            },
        },
        data() {
            return {
                meals: this.normalizeMeals(this.oldMeals),
            };
        },
        mounted() {
            this.notifyCountChanged();
        },
        watch: {
            // Each meal's hidden HTML5 validator gets `setCustomValidity('Veuillez…')`
            // on the first invalid submit. The constraint then sticks until we
            // explicitly clear it — re-running the form check would otherwise
            // keep blocking even after the user picks a restriction. We clear it
            // here whenever the underlying meals change.
            meals: {
                deep: true,
                handler() {
                    this.$nextTick(() => {
                        const validators = this.$el.querySelectorAll('.lunchDayMeals__restrictions-validator');
                        validators.forEach((validator) => {
                            if (validator.value && validator.validity && validator.validity.customError) {
                                validator.setCustomValidity('');
                            }
                        });
                    });
                },
            },
        },
        methods: {
            namePrefix(index) {
                return 'exceptions[' + this.configDayId + '][' + index + ']';
            },
            restrictionPickedFor(meal) {
                return Boolean(
                    meal.no_restriction
                    || meal.vegan
                    || meal.vegetarian
                    || meal.lactose_free
                    || meal.gluten_free
                    || meal.nut_free
                );
            },
            onRestrictionInvalid(event) {
                event.target.setCustomValidity(
                    this.$attrs['restrictions-required-message']
                    || 'Veuillez sélectionner au moins une indication alimentaire.'
                );
            },
            normalizeMeals(input) {
                const arr = Array.isArray(input) ? input : Object.values(input || {});
                return arr.map(m => this.normalizeMeal(m));
            },
            normalizeMeal(m) {
                const meal = m || {};

                return {
                    firstname: meal.firstname || '',
                    lastname: meal.lastname || '',
                    no_restriction: isTruthyBool(meal.no_restriction),
                    vegan: isTruthyBool(meal.vegan),
                    vegetarian: isTruthyBool(meal.vegetarian),
                    lactose_free: isTruthyBool(meal.lactose_free),
                    gluten_free: isTruthyBool(meal.gluten_free),
                    nut_free: isTruthyBool(meal.nut_free),
                    allergies: meal.allergies || '',
                };
            },
            addMeal() {
                this.meals.push(this.normalizeMeal({}));
                this.notifyCountChanged();
            },
            deleteMeal(index) {
                this.meals.splice(index, 1);
                this.notifyCountChanged();
            },
            notifyCountChanged() {
                this.$emit('count-changed', {
                    dayId: this.configDayId,
                    count: this.meals.length,
                });
            },
        },
    };
</script>
